import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const FORGOT_PASSWORD_RESOURCES = [
    { type: 'link', rel: 'stylesheet', href: '/assets/vendor/font-awesome/css/fontawesome-all.min.css', id: 'forgot-password-page-fa' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.googleapis.com', id: 'forgot-password-page-gfont-preconnect1' },
    { type: 'link', rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'true', id: 'forgot-password-page-gfont-preconnect2' },
    { type: 'link', rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap', id: 'forgot-password-page-gfont-style' }
];

// Simple FormMessageDisplay component
const FormMessageDisplay = ({ message, type }) => {
    if (!message) return null;
    const baseStyle = {
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '0.9rem',
        fontWeight: '500',
        marginBottom: '20px',
        textAlign: 'left',
        border: '1px solid transparent'
    };
    const styleMap = {
        error: { ...baseStyle, backgroundColor: '#fee2e2', color: '#ef4444', borderColor: '#fca5a5' },
        success: { ...baseStyle, backgroundColor: '#e6f9f3', color: '#0bc184', borderColor: '#a7f3d0' },
        info: { ...baseStyle, backgroundColor: '#eff6ff', color: '#25a5f0', borderColor: '#bfdbfe' }
    };
    const style = styleMap[type] || styleMap.info;
    return <div style={style} role="alert">{message}</div>;
};

function ForgotPassword() {
    // Set page title and inject resources if needed
    useEffect(() => {
        document.title = "Forgot Password";
        FORGOT_PASSWORD_RESOURCES.forEach(attrs => {
            if (!document.getElementById(attrs.id)) {
                const el = document.createElement(attrs.type);
                Object.keys(attrs).forEach(key => { if (key !== 'type') el[key] = attrs[key]; });
                document.head.appendChild(el);
            }
        });
        return () => {
            FORGOT_PASSWORD_RESOURCES.forEach(attrs => {
                const el = document.getElementById(attrs.id);
                if (el) el.remove();
            });
        };
    }, []);

    const [email, setEmail] = useState('');
    const [formMessage, setFormMessage] = useState({ text: '', type: '' });
    const [isLoading, setIsLoading] = useState(false);

    const handlePasswordResetRequest = async (e) => {
        e.preventDefault();
        setFormMessage({ text: '', type: '' });
        if (!email.trim()) {
            setFormMessage({ text: 'Email address is required.', type: 'error' });
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setFormMessage({ text: 'Please enter a valid email address.', type: 'error' });
            return;
        }
        setIsLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/vendors/forgot-password`;
            const response = await axios.post(apiUrl, { email });
            const successMessage = response.data.message || "If an account with that email exists, a password reset link has been sent.";
            setFormMessage({ text: successMessage, type: 'success' });
            setEmail('');
        } catch (err) {
            const errorMessage = err.response?.data?.message || "An error occurred. Please try again later.";
            setFormMessage({ text: errorMessage, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <style>{`
                :root {
                    --primary: #4f46e5;
                    --primary-hover: #4338ca;
                    --secondary-blue: #25a5f0;
                    --secondary-blue-hover: #1d8fd6;
                    --success: #0bc184;
                    --danger: #ef4444;
                    --border-color: #cbd5e1;
                    --text-dark: #1f2937;
                    --text-muted: #6b7280;
                    --bg-color: #f8fafc;
                }

                .login-page-body {
                    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
                    background-color: var(--bg-color);
                    color: var(--text-dark);
                    margin: 0;
                    padding: 20px; 
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 100vh;
                    box-sizing: border-box;
                }

                .portal-wrapper {
                    width: 100%;
                    max-width: 460px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .logo-container {
                    margin-bottom: 22px; 
                    text-align: center;
                }

                .logo-container img {
                    max-width: 210px; 
                    height: auto;
                    display: block;
                }

                .auth-card {
                    background: #ffffff;
                    width: 100%;
                    padding: 35px; 
                    border-radius: 16px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
                    border: 1px solid #e5e7eb;
                    box-sizing: border-box;
                }

                .header h2 {
                    font-size: 1.85rem;
                    margin: 0 0 6px 0;
                    font-weight: 700;
                    color: #111827;
                }

                .header p {
                    color: var(--text-muted);
                    margin-bottom: 26px; 
                    font-size: 0.95rem;
                    line-height: 1.4;
                }

                .form-group {
                    margin-bottom: 20px;
                    position: relative;
                }

                label {
                    display: block;
                    font-weight: 600;
                    margin-bottom: 8px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #374151;
                }

                input[type="text"], input[type="email"] {
                    width: 100%;
                    padding: 14px;
                    border: 1px solid var(--border-color);
                    border-radius: 8px;
                    box-sizing: border-box;
                    font-size: 1rem;
                    color: var(--text-dark);
                    transition: all 0.2s;
                }

                input:focus {
                    outline: none;
                    border-color: var(--primary);
                    box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.12);
                }

                .btn {
                    width: 100%;
                    border: none;
                    padding: 14px;
                    font-size: 1rem;
                    font-weight: 600;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background 0.2s, opacity 0.2s;
                    box-sizing: border-box;
                }
                .btn:hover {
                    color: #fff;
                }
                .auth-card .btn-primary-wizard {
                    background-color: #25a5f0;
                    background-image: none;
                    color: #ffffff;
                }
                .auth-card .btn-primary-wizard:hover:not(:disabled) {
                    background-color: #1d8fd6;
                    color: #ffffff;
                }
                .auth-card .btn-primary-wizard:disabled,
                .auth-card .btn-primary-wizard.disabled {
                    background-color: #25a5f0;
                    color: #ffffff;
                    cursor: not-allowed;
                    opacity: 0.65;
                }

                .footer-switch-text {
                    text-align: center;
                    margin-top: 25px;
                    font-size: 0.95rem;
                    color: var(--text-dark);
                }
                .footer-switch-text a {
                    color: var(--secondary-blue);
                    text-decoration: none;
                    font-weight: 600;
                    cursor: pointer;
                }
                .footer-switch-text a:hover { text-decoration: underline; }
            `}</style>
            <div className="login-page-body">
                <div className="portal-wrapper">
                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo-mangoloops.png" alt="MangoLoops Logo" />
                        </Link>
                    </div>
                    <div className="auth-card">
                        <div className="header">
                            <h2>Forgot Password</h2>
                            <p>Enter your email address to receive a password reset link.</p>
                        </div>
                        <FormMessageDisplay message={formMessage.text} type={formMessage.type} />
                        <form onSubmit={handlePasswordResetRequest}>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    placeholder="yourname@example.com"
                                    value={email}
                                    onChange={(e) => {
                                        setFormMessage({ text: '', type: '' });
                                        setEmail(e.target.value);
                                    }}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <button type="submit" className="btn btn-primary-wizard" disabled={isLoading}>
                                {isLoading ? 'Sending...' : 'Send Reset Link'}
                            </button>
                        </form>
                        <div className="footer-switch-text">
                            Remembered your password? <Link to="/login">Sign in</Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ForgotPassword;
